import { InjectionToken } from '@angular/core';
import { ObscuredData } from '@se/components';
import { Json, JsonObject } from '@se/cp.common';
import { EMPTY, Observable } from 'rxjs';

import { AuthData } from '../confirm/auth-data';
import { EcgChartState, ISeSignResultData, SeMultiSignInfo, SeSignInfo, SissData } from '../sign/estensasign.model';
import { ISissObscurationInfo, ISissPatientInfo, ISissReportInfo } from 'estensa-module';

export const REPORT_WORKFLOW_EXAM_SERVICE_TOKEN = new InjectionToken('REPORT_WORKFLOW_EXAM_SERVICE_TOKEN');

export interface IReport {
    id: string;
    isAdmitted: boolean;
    isReported: boolean;
    isApproved: boolean;
    isDigitallySigned: boolean;
    reportDetailForNotification: string;
}

export enum SignResultStatus {
    Failed = 0,
    Ok = 1,
    PdfGenerationFailed = 2,
    P7mGenerationFailed = 3,
    Locked = 4,
    Unlocked = 5,
}

export interface Addendum {
    id?: string;
    idClinicalReport?: string;
    idUser: string;
    text: string;
    date?: string;
    idReferringPhysician: any;
    idSecondReferringPhysician?: string;
    isDigitallySigned: boolean;
    idDigitallySigner?: string;
    digitallySignerCode?: string;
    digitallySigningDate?: string;
    isDraft?: boolean;
    isDeleting?: boolean;
}

export interface UnlockData {
    idExamination: string;
    idReport?: string;
}

export interface IResponseStatus<T = IReport> {
    id: string;
    dto: T;
    responseStatusType: SignResultStatus;
    error: {
        idExamination: string;
        message: string;
        userFullName?: string;
        hResult?: number;
        innerError?: string;
        internalMessage?: string;
        exceptionName?: string;
        stackTrace?: string;
        source?: string;
    };
    additionalInfo?: {
        domain: string;
        username: string;
        password: string | null;
        clientId: string;
    };
}

export interface HardCodedDataUpdate {
    idClinicalReport: string;
    hardCodedData: ObscuredData;
}

export interface SissUploadDocumentData {
    reportIds: string[];
    reportDescrList: string[];
    signerId: string;
    signerUsername: string;
    token: string;
    reportDocumentiList: {
        reportInfo: ISissReportInfo;
        patientInfo: ISissPatientInfo;
        obscurationInfo: ISissObscurationInfo;
    }[];
    idStruttura: string;
    idSubStruttura: string;
    codBrancaSpecialistica: string;
}

/*
Usare uqesta interfaccia nel metodo approve qui sotto, c'è da mettere a posto il mondo e mezzo di
metodi fatti senza usare i tipi....

export interface IApproveInfo {
    reportsIds: string[],
    authData: AuthData,
    isDeferred?: boolean,
    isFromWorklist?: boolean,
    displayedSettings?: EcgChartState,
    otherCredentialsData?: AuthData[],
    referringPhysicianId?: string
}
*/

export interface IReportWorkflowExamService {
    approve(
        reportsIds: string[],
        authData: AuthData,
        isDeferred?: boolean,
        isFromWorklist?: boolean,
        displayedSettings?: EcgChartState,
        otherCredentialsData?: AuthData[],
        referringPhysicianId?: string
    ): Observable<IResponseStatus[]>;

    getDataForSissSign(reportId: string): Observable<SissData>;

    uploadReportToSissCloud(data: SissUploadDocumentData): Observable<ISeSignResultData[]>;

    uploadReportToSissWayCloud(data: SissUploadDocumentData): Observable<ISeSignResultData[]>;

    getPdf(info: SeSignInfo): Observable<unknown>;

    sign(info: SeSignInfo): Observable<ISeSignResultData[]>;

    multiSign(info: SeMultiSignInfo): Observable<ISeSignResultData[]>;

    undoValidation(reportId: string, authData: AuthData, isFromWorklist?: boolean): Observable<unknown>;

    undoMultiValidation(reportId: string, authData: AuthData[], isFromWorklist?: boolean): Observable<IResponseStatus>;

    forceUnlockMultiple(data: UnlockData[]): Observable<unknown>;

    unlock(data: UnlockData[]): Observable<unknown>;

    getReportExaminations(idReport: string): Observable<any>;

    saveAddendum(addendum: Addendum): Observable<Addendum>;

    deleteAddendum(addendum: Addendum): Observable<Addendum>;

    saveHardCodedData(data: HardCodedDataUpdate): Observable<ObscuredData>;

    burnCd?(idExaminations: string[]): Observable<any>;

    approveJson?(data: JsonObject): Observable<Json>;

    saveJson ?(data : JsonObject) : Observable<Json>;

    suspend ?(idExaminations : string[], suspensionReasonId: string) : Observable<any>;

    undoSuspension ?(idExaminations : string[]) : Observable<any>;
}

export class NullReportWorkflowExamService implements IReportWorkflowExamService {
    approve() {
        return EMPTY;
    }

    getDataForSissSign() {
        return EMPTY;
    }

    uploadReportToSissCloud() {
        return EMPTY;
    }

    uploadReportToSissWayCloud() {
        return EMPTY;
    }

    getPdf() {
        return EMPTY;
    }

    sign() {
        return EMPTY;
    }

    multiSign() {
        return EMPTY;
    }

    undoValidation() {
        return EMPTY;
    }

    undoMultiValidation() {
        return EMPTY;
    }

    forceUnlockMultiple() {
        return EMPTY;
    }

    unlock() {
        return EMPTY;
    }

    getReportExaminations() {
        return EMPTY;
    }

    saveAddendum() {
        return EMPTY;
    }

    deleteAddendum() {
        return EMPTY;
    }

    saveHardCodedData() {
        return EMPTY;
    }

    suspend () {
        return EMPTY;
    }

    undoSuspension () {
        return EMPTY;
    }
}
